<template>
  <div class="ask-ai-page">
    <header class="header">
      <div class="logo">
        <img src="@/assets/taxscanai-logo.png" alt="Taxscan Logo" />
      </div>
      <button class="logout-button" @click="logout">
        <span class="desktop-label">Signout</span>
        <span class="mobile-label">S</span>
      </button>
    </header>

    <div class="content-container">
      <div class="page-header">
        <!-- Center align all text if credit is below 100 -->
        <div :class="['balance-text', balanceClass, { 'center-text': creditBalance > 0 && creditBalance < 100 }]">
          You have a balance of ₹{{ creditBalance }}.
          <template v-if="creditBalance > 0 && creditBalance < 100">
            <span class="low-credit-prompt">
              Please add credits to continue enjoying our services:
            </span>
            <div class="credit-options">
              <button
                v-for="option in creditOptions"
                :key="option"
                @click="initiatePayment(option)"
                class="credit-button"
              >
                Add ₹{{ option }}
              </button>
            </div>
          </template>
        </div>
      </div>

      <div class="question-card">
        <h1>Ask AI Your Question</h1>
        <textarea
          id="question"
          placeholder="Enter your question..."
          rows="12"
          maxlength="1000"
          v-model="question"
        ></textarea>
        <p class="instructions">
          You can ask anything related to tax laws, judgments, or general queries.
        </p>
        <p class="char-limit">{{ question.length }}/1000</p>
        <p v-if="creditBalance <= 0" class="credit-warning">
          Your credit is below zero. Please top up to continue.
        </p>
        <button
          class="primary-button"
          @click="submitQuestion"
          :disabled="creditBalance <= 0"
        >
          Submit
        </button>
        <p v-if="error" class="error-message">{{ error }}</p>
      </div>

      <!-- Payment element container -->
      <div id="cashfree-container" v-if="showPaymentElement"></div>
    </div>

    <footer class="footer">
      <p>© {{ new Date().getFullYear() }} Taxscan Edutech Private Limited. All rights reserved.</p>
    </footer>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AskAIPage',
  data() {
    return {
      question: '',
      error: '',
      creditOptions: [2500, 5000, 7500, 10000],
      showPaymentElement: false,
      currentPaymentSession: null,
      currentOrderId: null
    };
  },
  computed: {
    creditBalance() {
      const balance = localStorage.getItem('credit_balance') || '0';
      return parseFloat(balance);
    },
    balanceClass() {
      if (this.creditBalance < 50) {
        return 'red-balance';
      } else if (this.creditBalance < 100) {
        return 'yellow-balance';
      } else {
        return 'green-balance';
      }
    }
  },
  methods: {
    async submitQuestion() {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        this.error = 'Authentication failed. Please login again.';
        this.$router.push('/login');
        return;
      }
      if (!this.question.trim()) {
        this.error = 'Please enter a question!';
        return;
      }
      localStorage.setItem('user_question', this.question.trim());
      this.$router.push('/context').catch((err) => {
        console.error('Navigation error:', err);
      });
    },
    logout() {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('credit_balance');
      localStorage.removeItem('user_question');
      this.$router.push('/login');
    },
    async initiatePayment(amount) {
      try {
        // Retrieve token and user ID (assumed to be stored on login)
        const token = localStorage.getItem('auth_token');
        const userId = localStorage.getItem('user_id');
        // Call your backend to create a Cashfree payment session
        const response = await axios.post(
          '/create-payment-session',
          { amount, userId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const { paymentSessionId, orderId } = response.data;
        this.currentPaymentSession = paymentSessionId;
        this.currentOrderId = orderId;
        this.showPaymentElement = true;
        // Ensure the container is rendered before initializing Cashfree
        this.$nextTick(() => {
          if (window.Cashfree) {
            // Initialize Cashfree Payment Element with necessary callbacks
            window.Cashfree.init({
              payment_session_id: paymentSessionId,
              order_id: orderId,
              onSuccess: this.onPaymentSuccess,
              onError: this.onPaymentError,
              onClose: this.onPaymentClose,
            });
            window.Cashfree.render('cashfree-container');
          } else {
            // Fallback: redirect to Cashfree's checkout page if SDK isn't loaded
            window.location.href = `https://checkout.cashfree.com?payment_session_id=${paymentSessionId}&order_id=${orderId}`;
          }
        });
      } catch (err) {
        console.error('Error initiating payment:', err);
        this.error = 'Failed to initiate payment. Please try again.';
      }
    },
    onPaymentSuccess(response) {
      console.log('Payment successful', response);
      alert("Payment Successful!");
      // After a successful payment, you might want to update the credit balance
      // For example, re-fetch the user's details from the backend or update localStorage.
      this.showPaymentElement = false;
    },
    onPaymentError(error) {
      console.error('Payment error', error);
      alert("Payment failed. Please try again.");
      this.showPaymentElement = false;
    },
    onPaymentClose() {
      console.log('Payment widget closed');
      this.showPaymentElement = false;
    }
  },
  mounted() {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      this.error = 'Authentication failed. Please login again.';
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
/* 
  Below are your existing or similar styles, preserved.
  Adjust as needed to match your original design.
*/
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

.ask-ai-page {
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  padding-top: 10px;
}

.center-text {
  text-align: center;
}


.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px; /* fixed optimal height as per bestdoc */
  z-index: 1000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px; /* horizontal padding only, zero vertical padding */
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}


.logo img {
  max-height: 140px;
  transition: max-height 0.3s;
}

.content-container {
  max-width: 800px;
  margin: 10px auto 20px auto;
  padding: 0 20px;
}

.page-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.balance-text {
  font-size: 16px;
  font-weight: 500;
  text-align: center;
}

/* Color classes for different credit ranges */
.red-balance {
  color: red;
}
.yellow-balance {
  color: #d4c100; /* a gold-like yellow */
}
.green-balance {
  color: green;
}

.credit-display {
  margin-right: 20px;
  font-size: 16px;
  font-weight: bold;
  color: #333;
}


/* The card-like container for the question input */
.question-card {
  background: #ffffff;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

/* Heading, label, and instructions styling */
.question-card h1 {
  margin-bottom: 20px;
  font-size: 1.5rem;
}

.question-card label {
  font-weight: 600;
  margin-bottom: 10px;
}

/* Textarea for the question */
textarea {
  width: 100%;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #ddd;
  resize: none;
  box-sizing: border-box;
}

/* Primary button styling */
.primary-button {
  background: #007bff;
  color: #fff;
  border: none;
  padding: 12px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}
.primary-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Error messages and warnings */
.error-message,
.credit-warning {
  color: red;
  margin-top: 10px;
}

.logout-button {
  background: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  white-space: nowrap; 
}

.logout-button:hover {
  background: #0056b3;
}

.logout-button .mobile-label {
  display: none;
}

.credit-options {
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 12px; /* Increased gap between buttons */
  }

  .credit-button {
    padding: 10px 20px;
    background: linear-gradient(135deg, #007bff, #00c6ff); /* Colorful gradient matching the theme */
    border: none;
    border-radius: 6px;
    color: #fff;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .credit-button:hover {
    background: linear-gradient(135deg, #0056b3, #009acf); /* Darker gradient on hover */
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  .credit-button:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .low-credit-prompt {
    display: block;
    margin-top: 5px;
    font-size: 0.9rem;
    color: #333;
  }
  #cashfree-container {
    margin-top: 20px;
  }

@media (max-width: 768px) {
  .logo img {
    max-height: 100px;
  }
  .logout-button .desktop-label {
    display: none;
  }
  .logout-button .mobile-label {
    display: inline;
  }
  .footer {
    font-size: 11px; /* or any size you prefer for mobile devices */
  }
  
}



</style>